import { PRODUCTION_WIDGET_RUNTIME_ENDPOINTS } from "@getuserfeedback/protocol/host";

const trimOptionalUrl = (value: string | undefined): string | undefined => {
	if (typeof value !== "string") {
		return;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

const parseAbsoluteUrl = (
	value: string,
	label: "_loaderUrl" | "_coreUrl" | "_apiUrl" | "_realtimeUrl",
): string => {
	try {
		return new URL(value).toString();
	} catch {
		throw new Error(`${label} must be an absolute URL`);
	}
};

const buildFirstPartyProductionUrls = (
	apiKey: string,
): {
	loaderUrl: string;
	coreUrl: string;
	apiUrl: string;
} => ({
	loaderUrl: `https://cdn.getuserfeedback.com/widget/loader/v4/${encodeURIComponent(apiKey)}/loader.js`,
	coreUrl: PRODUCTION_WIDGET_RUNTIME_ENDPOINTS.coreUrl,
	apiUrl: PRODUCTION_WIDGET_RUNTIME_ENDPOINTS.apiUrl,
});

const buildRetainedV2BrowserUrls = (apiKey: string) => ({
	loaderUrl: `https://cdn.getuserfeedback.com/widget/loader/v2/${encodeURIComponent(apiKey)}/loader.js`,
	coreUrl: "https://cdn.getuserfeedback.com/widget/core/v2/core.html",
});

const buildRetainedV3BrowserUrls = (apiKey: string) => ({
	loaderUrl: `https://cdn.getuserfeedback.com/widget/loader/v3/${encodeURIComponent(apiKey)}/loader.js`,
	coreUrl: "https://cdn.getuserfeedback.com/widget/core/v5/core.html",
});

const resolveRetainedBrowserUrls = (
	apiKey: string,
	url: string | undefined,
): ReturnType<typeof buildRetainedV2BrowserUrls> | undefined => {
	if (
		url?.includes("/loader/v2/") === true ||
		url?.includes("/core/v2/") === true
	) {
		return buildRetainedV2BrowserUrls(apiKey);
	}
	if (
		url?.includes("/loader/v3/") === true ||
		url?.includes("/core/v5/") === true
	) {
		return buildRetainedV3BrowserUrls(apiKey);
	}
	return;
};

export const resolveStartupRuntimeUrls = (input: {
	apiKey: string;
	_loaderUrl: string | undefined;
	_coreUrl: string | undefined;
	_apiUrl: string | undefined;
	_realtimeUrl: string | undefined;
}): {
	loaderUrl: string;
	coreUrl: string;
	apiUrl: string;
	realtimeUrl?: string;
} => {
	const defaults = buildFirstPartyProductionUrls(input.apiKey);
	const loaderUrl = trimOptionalUrl(input._loaderUrl);
	const coreUrl = trimOptionalUrl(input._coreUrl);
	const apiUrl = trimOptionalUrl(input._apiUrl);
	const realtimeUrl = trimOptionalUrl(input._realtimeUrl);
	// A lone retained override must use its immutable release companion.
	const browserFallbacks =
		resolveRetainedBrowserUrls(input.apiKey, loaderUrl ?? coreUrl) ?? defaults;

	return {
		loaderUrl:
			loaderUrl !== undefined
				? parseAbsoluteUrl(loaderUrl, "_loaderUrl")
				: coreUrl !== undefined
					? browserFallbacks.loaderUrl
					: defaults.loaderUrl,
		coreUrl:
			coreUrl !== undefined
				? parseAbsoluteUrl(coreUrl, "_coreUrl")
				: loaderUrl !== undefined
					? browserFallbacks.coreUrl
					: defaults.coreUrl,
		apiUrl:
			apiUrl !== undefined
				? parseAbsoluteUrl(apiUrl, "_apiUrl")
				: defaults.apiUrl,
		...(realtimeUrl !== undefined
			? { realtimeUrl: parseAbsoluteUrl(realtimeUrl, "_realtimeUrl") }
			: {}),
	};
};
